import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';
import { Bar } from 'react-chartjs-2';
import { CalendarBlank } from '@phosphor-icons/react';
import { formatCurrency } from 'shared/utils/currency';
import { AcquisitionYearBucket } from '../types/statistics.types';
import { COMMON_TOOLTIP } from './chartConfig';
import { MetricInfo } from './MetricInfo';
import './chartConfig';

interface Props {
  data?: AcquisitionYearBucket[];
  loading: boolean;
}

export const YearlyTrendChart = ({ data, loading }: Props) => {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null;
    return {
      labels: data.map((d) => String(d.year)),
      datasets: [
        {
          type: 'bar' as const,
          label: 'Totaal dossiers',
          data: data.map((d) => d.count),
          backgroundColor: '#5B7304CC',
          borderColor: '#5B7304',
          borderWidth: 1,
          borderRadius: 4,
          yAxisID: 'yCount',
        },
        {
          type: 'bar' as const,
          label: 'Deals',
          data: data.map((d) => d.deals),
          backgroundColor: '#22C55ECC',
          borderColor: '#22C55E',
          borderWidth: 1,
          borderRadius: 4,
          yAxisID: 'yCount',
        },
        {
          type: 'bar' as const,
          label: 'Afgewezen',
          data: data.map((d) => d.rejected),
          backgroundColor: '#F4365ECC',
          borderColor: '#F43F5E',
          borderWidth: 1,
          borderRadius: 4,
          yAxisID: 'yCount',
        },
      ],
      _meta: data,
    };
  }, [data]);

  if (loading) {
    return (
      <Card className="bg-white">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <CalendarBlank className="h-5 w-5 text-moss-primary" weight="duotone" />
            <Skeleton className="h-5 w-40" />
          </div>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[320px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <CalendarBlank className="h-5 w-5 text-moss-primary" weight="duotone" />
          <CardTitle className="text-lg">Dossiers per jaar</CardTitle>
          <MetricInfo
            description={
              <div className="space-y-1">
                <p>
                  Aantal dossiers per kalenderjaar, gegroepeerd op{' '}
                  <code>createdAt</code>.
                </p>
                <p>
                  <strong>Totaal:</strong> alle aangemaakte dossiers in dat jaar.
                </p>
                <p>
                  <strong>Deals / Afgewezen:</strong> huidige uitkomst van die
                  cohort, ongeacht wanneer de deal/afwijzing daadwerkelijk
                  plaatsvond.
                </p>
              </div>
            }
          />
        </div>
        <p className="text-xs text-neutral-secondary-mid">
          Instroom en uitkomsten per kalenderjaar (op basis van aanmaakdatum)
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-[320px]">
          {chartData ? (
            <Bar
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: { boxWidth: 12, padding: 12, font: { size: 12 } },
                  },
                  tooltip: {
                    ...COMMON_TOOLTIP,
                    callbacks: {
                      afterBody: (items: any[]) => {
                        if (!items.length) return [];
                        const idx = items[0].dataIndex;
                        const meta = chartData._meta[idx];
                        return [
                          '',
                          `Volume totaal: ${formatCurrency(meta.totalVolume)}`,
                          `Volume deals: ${formatCurrency(meta.dealVolume)}`,
                        ];
                      },
                    },
                  },
                },
                scales: {
                  x: { grid: { display: false } },
                  yCount: {
                    type: 'linear',
                    position: 'left',
                    beginAtZero: true,
                    title: { display: true, text: '# dossiers' },
                    ticks: { precision: 0 },
                    grid: { color: 'rgba(49, 64, 59, 0.1)' },
                  },
                },
              }}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-neutral-secondary-mid">
              Geen data beschikbaar
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
